import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrgFinancingProfile } from '@hero/schema';
import { useOrgProfileApi } from '@hero/api-hooks';
import type { ApplyFormValues } from '../ApplyForm/types';
import { profileToFormValues } from '../../utils/financingProfile';
import { FinancingProfileDrawer } from './FinancingProfileDrawer';
import { RequirementsCard } from './RequirementsCard';
import { financingProfileChecks, isFinancingProfileComplete } from '../../utils/financingProfile';

export type FinancingProfileRequirementCardProps = {
  onChange: <K extends keyof ApplyFormValues>(
    key: K,
    value: ApplyFormValues[K],
  ) => void;
  /** Parent refetch so Terms-step Continue gating updates immediately. */
  onProfileSaved?: () => void;
};

/**
 * Requirement card + drawer for `Org.financingProfile`. Sits at the
 * top of the Terms step alongside carrier legal / banking gates.
 * Refetches the saved profile every time the Terms step mounts.
 */
export function FinancingProfileRequirementCard({
  onChange,
  onProfileSaved,
}: FinancingProfileRequirementCardProps) {
  const { t } = useTranslation('m-financing');
  /**
   * `Org.financingProfile` is now a section of the unified
   * `useOrgProfileApi()` projection — no dedicated endpoint.
   */
  const profileQuery = useOrgProfileApi();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    void profileQuery.refetch();
  }, []);

  const savedProfile = profileQuery.data?.financingProfile ?? null;
  const complete = isFinancingProfileComplete(savedProfile);

  const missingItems = useMemo(() => {
    const checks = financingProfileChecks(savedProfile);
    return [
      !checks.fleetTier &&
        t('apply_form.profile.requirements.missing.fleet_tier'),
      !checks.annualRevenueTier &&
        t('apply_form.profile.requirements.missing.annual_revenue'),
      !checks.ownsAllTrucks &&
        t('apply_form.profile.requirements.missing.owns_all_trucks'),
      !checks.trucksOwnedCount &&
        t('apply_form.profile.requirements.missing.trucks_owned_count'),
      !checks.hasLoans && t('apply_form.profile.requirements.missing.has_loans'),
      !checks.monthlyFinancingPayment &&
        t('apply_form.profile.requirements.missing.monthly_payment'),
    ].filter(Boolean) as string[];
  }, [savedProfile, t]);

  const syncFormFromProfile = (profile: OrgFinancingProfile) => {
    const patch = profileToFormValues(profile);
    (Object.keys(patch) as Array<keyof ApplyFormValues>).forEach(key => {
      const value = patch[key];
      if (value !== undefined) {
        onChange(key, value as ApplyFormValues[typeof key]);
      }
    });
  };

  useEffect(() => {
    if (savedProfile) {
      syncFormFromProfile(savedProfile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedProfile]);

  return (
    <>
      <RequirementsCard
        title={t('apply_form.profile.requirements.title')}
        description={t('apply_form.profile.requirements.description')}
        complete={complete}
        level="hard"
        actionLabel={t('apply_form.profile.requirements.action')}
        onAction={() => setIsDrawerOpen(true)}
        missingItems={missingItems}
        disabled={profileQuery.isLoading}
      />

      <FinancingProfileDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        savedProfile={savedProfile}
        onSaved={profile => {
          syncFormFromProfile(profile);
          void profileQuery.refetch();
          onProfileSaved?.();
        }}
      />
    </>
  );
}
